import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import InboundCredentials from '../components/integrations/InboundCredentials';
import {
    Plus, RefreshCw, Trash2, Edit3, Zap, CheckCircle2, XCircle,
    Copy, Activity,
} from 'lucide-react';

/**
 * Integrations
 * ------------
 * Landlord-facing UI for wiring up bank / microfinance APIs.
 * Ships with a Family Bank driver; other providers appear in the list
 * as soon as their driver is registered on the backend.
 */
const Integrations = () => {
    const [integrations, setIntegrations] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [txDrawer, setTxDrawer] = useState(null); // integration id

    const loadAll = async () => {
        setLoading(true);
        try {
            const [i, p] = await Promise.all([
                axios.get('/api/integrations'),
                axios.get('/api/integrations/providers'),
            ]);
            setIntegrations(i.data.integrations || []);
            setProviders(p.data.providers || []);
        } catch (err) {
            toast.error('Failed to load integrations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);

    const handleTest = async (id) => {
        const t = toast.loading('Testing connection…');
        try {
            const { data } = await axios.post(`/api/integrations/${id}/test`);
            toast.success(`OK — token expires ${new Date(data.expires_at).toLocaleTimeString()}`, { id: t });
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Connection failed', { id: t });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this integration? Historical transactions will also be removed.')) return;
        try {
            await axios.delete(`/api/integrations/${id}`);
            toast.success('Integration deleted');
            loadAll();
        } catch {
            toast.error('Delete failed');
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Bank &amp; Microfinance Integrations</h1>
                    <p className="text-gray-600 mt-1">
                        Connect your accounts so rent payments post automatically.
                    </p>
                </div>
                <button
                    onClick={() => { setEditing(null); setShowForm(true); }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                    <Plus className="h-4 w-4" /> New integration
                </button>
            </div>

            {loading ? (
                <div className="text-gray-500">Loading…</div>
            ) : integrations.length === 0 ? (
                <EmptyState onCreate={() => { setEditing(null); setShowForm(true); }} />
            ) : (
                <div className="grid gap-4">
                    {integrations.map(i => (
                        <IntegrationCard
                            key={i.id}
                            integration={i}
                            onTest={() => handleTest(i.id)}
                            onEdit={() => { setEditing(i); setShowForm(true); }}
                            onDelete={() => handleDelete(i.id)}
                            onViewTx={() => setTxDrawer(i.id)}
                            onSaved={loadAll}
                        />
                    ))}
                </div>
            )}

            {showForm && (
                <IntegrationForm
                    providers={providers}
                    integration={editing}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); loadAll(); }}
                />
            )}

            {txDrawer && (
                <TransactionsDrawer
                    integrationId={txDrawer}
                    onClose={() => setTxDrawer(null)}
                />
            )}
        </div>
    );
};

/* ---------- sub-components ---------- */

const EmptyState = ({ onCreate }) => (
    <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
        <Zap className="h-10 w-10 mx-auto text-primary-500" />
        <h3 className="mt-3 text-lg font-semibold">No integrations yet</h3>
        <p className="mt-1 text-gray-600">
            Add your Family Bank OpenBank collection account, or another bank / microfinance,
            to receive rent payments automatically.
        </p>
        <button onClick={onCreate} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
            <Plus className="h-4 w-4" /> Add your first integration
        </button>
    </div>
);

const IntegrationCard = ({ integration, onTest, onEdit, onDelete, onViewTx, onSaved }) => {
    const webhookBase = `${window.location.origin.replace(/\/$/, '')}/webhooks/integrations/${integration.id}`;
    const copy = (v) => { navigator.clipboard.writeText(v); toast.success('Copied'); };
    return (
        <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
            <div className="flex flex-wrap justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">{integration.display_name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${integration.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {integration.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 capitalize">
                            {integration.environment}
                        </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                        {integration.provider_name}
                        {integration.last_success_at && (
                            <> · Last success {new Date(integration.last_success_at).toLocaleString()}</>
                        )}
                    </p>
                    {integration.last_error && (
                        <p className="text-sm text-red-600 mt-1">⚠ {integration.last_error}</p>
                    )}
                </div>
                <div className="flex gap-2">
                    <button onClick={onTest} title="Test connection"
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
                        <RefreshCw className="h-4 w-4" />
                    </button>
                    <button onClick={onViewTx} title="Transactions"
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
                        <Activity className="h-4 w-4" />
                    </button>
                    <button onClick={onEdit} title="Edit"
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
                        <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={onDelete} title="Delete"
                        className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="mt-4 grid gap-2 text-sm">
                <WebhookRow label="Collections webhook (VALIDATION + PAYMENT_NOTIFICATION)"
                    url={`${webhookBase}/collections`} onCopy={copy} />
                <WebhookRow label="M-Pesa C2B IPN callback"
                    url={`${webhookBase}/mpesa`} onCopy={copy} />
                {integration.driver === 'mpesa_daraja' && (
                    <WebhookRow label="Daraja STK callback"
                        url={`${webhookBase}/mpesa-stk`} onCopy={copy} />
                )}
                <WebhookRow label="OAuth2 token endpoint (client_credentials)"
                    url={`${webhookBase}/token`} onCopy={copy} />
            </div>

            <InboundCredentials integration={integration} onSaved={onSaved} />
        </div>
    );
};

const WebhookRow = ({ label, url, onCopy }) => (
    <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs">
            <span className="truncate flex-1">{url}</span>
            <button onClick={() => onCopy(url)} className="text-gray-500 hover:text-gray-800">
                <Copy className="h-3.5 w-3.5" />
            </button>
        </div>
    </div>
);

/* ---------- create / edit form ---------- */

const DRIVER_FIELDS = {
    familybank: [
        { key: 'auth_url',          label: 'OAuth2 Access Token URL', required: true },
        { key: 'client_id',         label: 'Client ID',               required: true },
        { key: 'client_secret',     label: 'Client Secret',           required: true, secret: true },
        { key: 'scope',             label: 'Scope',                   default: 'FBL_COLLECTIONS' },
        { key: 'collection_account',label: 'Collection Account',      required: true },
        { key: 'inbound_username',  label: 'Webhook Basic Auth Username', required: true,
          help: 'Provide these to Family Bank so their servers can call us back.' },
        { key: 'inbound_password',  label: 'Webhook Basic Auth Password', required: true, secret: true },
    ],
    familybank_mpesa: [
        { key: 'inbound_username',  label: 'Webhook Basic Auth Username', required: true },
        { key: 'inbound_password',  label: 'Webhook Basic Auth Password', required: true, secret: true },
    ],
    mpesa_daraja: [
        { key: 'consumer_key',      label: 'Consumer Key', required: true, secret: true,
          help: 'Safaricom Daraja app Consumer Key.' },
        { key: 'consumer_secret',   label: 'Consumer Secret', required: true, secret: true,
          help: 'Safaricom Daraja app Consumer Secret.' },
        { key: 'shortcode',         label: 'Business Shortcode', required: true,
          help: 'Your Daraja Paybill/Till shortcode.' },
        { key: 'passkey',           label: 'Passkey', required: true, secret: true,
          help: 'Safaricom Passkey for STK Push.' },
        { key: 'callback_url',      label: 'STK Callback URL', required: true,
          help: 'Use the Daraja STK callback URL shown in this page, e.g. https://your-domain.com/webhooks/integrations/{id}/mpesa-stk' },
        { key: 'account_reference_prefix', label: 'Account Reference Prefix', default: 'RENT' },
        { key: 'transaction_desc',  label: 'Transaction Description', default: 'Rent Payment' },
    ],
};

const IntegrationForm = ({ providers, integration, onClose, onSaved }) => {
    const isEdit = !!integration;
    const [providerId, setProviderId] = useState(integration?.provider_id || '');
    const [displayName, setDisplayName] = useState(integration?.display_name || '');
    const [environment, setEnvironment] = useState(integration?.environment || 'sandbox');
    const [config, setConfig] = useState(integration?.config || {});
    const [saving, setSaving] = useState(false);

    const chosen = providers.find(p => p.id === Number(providerId));
    const fields = chosen ? (DRIVER_FIELDS[chosen.driver] || []) : [];

    const setField = (k, v) => setConfig(c => ({ ...c, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!providerId || !displayName) { toast.error('Provider and name are required'); return; }
        setSaving(true);
        try {
            const body = { provider_id: Number(providerId), display_name: displayName, environment, config };
            if (isEdit) {
                await axios.put(`/api/integrations/${integration.id}`, body);
                toast.success('Integration updated');
            } else {
                await axios.post('/api/integrations', body);
                toast.success('Integration created');
            }
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                        {isEdit ? 'Edit integration' : 'New integration'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
                </div>

                <form onSubmit={submit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Provider</label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            value={providerId}
                            onChange={(e) => setProviderId(e.target.value)}
                            disabled={isEdit}
                        >
                            <option value="">Select a provider…</option>
                            {providers.map(p => (
                                <option key={p.id} value={p.id} disabled={!p.implemented}>
                                    {p.name} {!p.implemented && '(coming soon)'}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Display name</label>
                            <input
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder="e.g. Family Bank – Main Collection A/C"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Environment</label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                value={environment}
                                onChange={(e) => setEnvironment(e.target.value)}
                            >
                                <option value="sandbox">Sandbox</option>
                                <option value="production">Production</option>
                            </select>
                        </div>
                    </div>

                    {fields.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-800 mb-2">Provider credentials</h3>
                            <div className="grid grid-cols-1 gap-3">
                                {fields.map(f => (
                                    <div key={f.key}>
                                        <label className="block text-sm font-medium mb-1">
                                            {f.label}{f.required && <span className="text-red-500"> *</span>}
                                        </label>
                                        <input
                                            type={f.secret ? 'password' : 'text'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                            value={config[f.key] ?? f.default ?? ''}
                                            onChange={(e) => setField(f.key, e.target.value)}
                                            placeholder={f.default || ''}
                                            autoComplete="off"
                                        />
                                        {f.help && <p className="text-xs text-gray-500 mt-1">{f.help}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-primary-600 text-white disabled:opacity-50">
                            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ---------- transactions drawer ---------- */

const TransactionsDrawer = ({ integrationId, onClose }) => {
    const [txs, setTxs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`/api/integrations/${integrationId}/transactions?limit=100`);
                setTxs(data.transactions || []);
            } catch { toast.error('Failed to load transactions'); }
            finally { setLoading(false); }
        })();
    }, [integrationId]);

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
            <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="text-lg font-semibold">Recent transactions</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
                </div>
                {loading ? (
                    <div className="p-6 text-gray-500">Loading…</div>
                ) : txs.length === 0 ? (
                    <div className="p-6 text-gray-500">No transactions yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left">
                            <tr>
                                <th className="p-2">When</th>
                                <th className="p-2">Action</th>
                                <th className="p-2">Ref</th>
                                <th className="p-2">Amount</th>
                                <th className="p-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {txs.map(t => (
                                <tr key={t.id} className="border-t border-gray-100">
                                    <td className="p-2 whitespace-nowrap">{new Date(t.received_at).toLocaleString()}</td>
                                    <td className="p-2">{t.action}</td>
                                    <td className="p-2 font-mono text-xs">{t.external_reference || '—'}</td>
                                    <td className="p-2">{t.amount ? `${t.currency} ${Number(t.amount).toFixed(2)}` : '—'}</td>
                                    <td className="p-2">
                                        {t.status === 'posted' || t.status === 'validated' ? (
                                            <span className="inline-flex items-center gap-1 text-green-700">
                                                <CheckCircle2 className="h-4 w-4" /> {t.status}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-red-600">
                                                <XCircle className="h-4 w-4" /> {t.status}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Integrations;
